import React, { useMemo } from 'react';
import { BeveledPanel } from '../Equipment/FabProcessTool/BeveledPanel';
import { getStockerMaterials } from './StockerMaterials';
import type { StockerDimensions } from './stockerTypes';

export type StockerBodyProps = {
  dims: StockerDimensions;
  opacity?: number;
};

/**
 * Reference stocker shell — open front (−Z), glass on sides/back via StockerGlass.
 * Load bay uses one continuous black frame (no vertical strips).
 */
export const StockerBody: React.FC<StockerBodyProps> = ({ dims, opacity = 1 }) => {
  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);
  const {
    width,
    depth,
    baseH,
    capH,
    pillarBottom,
    pillarTop,
    loadPortTopY,
    frontFaceZ,
    frameT,
  } = dims;

  const hx = width * 0.46;
  const hz = depth * 0.46;
  const colW = frameT * 1.55;
  const colH = pillarTop - pillarBottom;
  const colY = (pillarTop + pillarBottom) * 0.5;
  const r = Math.min(width, depth) * 0.012;
  /** Full span between front corner pillars — no side gaps. */
  const loadBayW = 2 * (hx - colW * 0.52);
  const loadBayH = loadPortTopY - pillarBottom;
  const loadBayY = pillarBottom + loadBayH * 0.5;
  const loadFaceZ = frontFaceZ - frameT * 0.62;
  const loadFrameD = frameT * 2.2;
  const openW = loadBayW;

  const frontCorners = (
    [
      [-hx, -hz],
      [hx, -hz],
    ] as const
  );
  const rearCorners = (
    [
      [-hx, hz],
      [hx, hz],
    ] as const
  );

  return (
    <group name="StockerBody">
      <BeveledPanel
        size={[width * 1.01, baseH, depth * 0.99]}
        material={mats.navyBaseGlass}
        position={[0, baseH * 0.5, 0]}
        radius={r * 0.2}
        receiveShadow
      />
      <BeveledPanel
        size={[width * 0.99, frameT * 0.85, depth * 0.98]}
        material={mats.brushedMetal}
        position={[0, baseH + frameT * 0.42, 0]}
        radius={r * 0.25}
        castShadow
      />

      {[...frontCorners, ...rearCorners].map(([x, z], i) => {
        const isFront = z < 0;
        return (
          <group key={`pillar-${i}`}>
            <BeveledPanel
              size={[colW, colH, colW * 1.05]}
              material={isFront ? mats.darkMetal : mats.whiteMetal}
              position={[x, colY, z]}
              radius={r * 0.35}
              smoothness={3}
              castShadow
            />
          </group>
        );
      })}

      <BeveledPanel
        size={[width * 0.99, frameT, depth * 0.99]}
        material={mats.brushedMetal}
        position={[0, pillarTop - frameT * 0.5, 0]}
        radius={r * 0.3}
        castShadow
      />

      <BeveledPanel
        size={[width * 0.98, capH * 0.16, depth * 0.97]}
        material={mats.whiteMetal}
        position={[0, pillarTop + capH * 0.08, 0]}
        radius={r * 0.25}
        smoothness={3}
        castShadow
      />

      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`front-lintel-${side}`}
          size={[(width - openW) * 0.5, frameT, frameT * 0.9]}
          material={mats.darkMetal}
          position={[
            side * (openW * 0.5 + (width - openW) * 0.25),
            pillarTop - frameT * 1.15,
            frontFaceZ - frameT * 0.35,
          ]}
          radius={r * 0.25}
          castShadow
        />
      ))}

      {/* Load bay — one solid black wall, pillar-to-pillar, base to load-port lintel */}
      <BeveledPanel
        size={[loadBayW, loadBayH, loadFrameD]}
        material={mats.darkMetal}
        position={[0, loadBayY, loadFaceZ]}
        radius={r * 0.12}
        smoothness={2}
        castShadow
        receiveShadow
      />
      {/* Inner backing — blocks white interior bleed-through at edges */}
      <BeveledPanel
        size={[loadBayW * 0.995, loadBayH * 0.995, frameT * 0.65]}
        material={mats.darkMetal}
        position={[0, loadBayY, loadFaceZ + frameT * 0.35]}
        radius={r * 0.08}
      />
      {/* Top lintel — flush with load-port / rack divider (closes top gap) */}
      <BeveledPanel
        size={[loadBayW * 1.02, frameT * 1.35, loadFrameD * 1.05]}
        material={mats.darkMetal}
        position={[0, loadPortTopY - frameT * 0.68, loadFaceZ]}
        radius={r * 0.1}
        castShadow
      />
      {/* Bottom sill + base front cover (closes bottom gap and navy base bleed) */}
      <BeveledPanel
        size={[loadBayW * 1.02, frameT * 1.1, loadFrameD * 1.05]}
        material={mats.darkMetal}
        position={[0, pillarBottom + frameT * 0.55, loadFaceZ]}
        radius={r * 0.1}
        receiveShadow
      />
      <BeveledPanel
        size={[loadBayW * 1.01, Math.max(baseH, frameT * 0.85), loadFrameD * 1.08]}
        material={mats.darkMetal}
        position={[0, Math.max(baseH, frameT * 0.85) * 0.5, loadFaceZ - frameT * 0.08]}
        radius={r * 0.1}
        receiveShadow
      />
      {/* Side jamb fills — pillar inner faces (closes any remaining corner gap) */}
      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`load-jamb-${side}`}
          size={[colW * 0.95, loadBayH, loadFrameD * 0.92]}
          material={mats.darkMetal}
          position={[side * (hx - colW * 0.04), loadBayY, loadFaceZ]}
          radius={r * 0.08}
          castShadow
        />
      ))}
    </group>
  );
};

export default StockerBody;
